import { ReactNode } from 'react';
import { Icon, IconName } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, TableColumnConfig } from '@frontend/design-system';

export interface ValueWithTrendData {
  value: number;
  change: number;
}

export interface ValueWithTrendProps {
  /** The cell data containing a value and its trend change percentage. */
  cellValue: ValueWithTrendData;
  /** Optional prefix rendered before the value (e.g. "$"). */
  startAdornment?: string;
  /** Optional suffix rendered after the value (e.g. "%"). */
  endAdornment?: string;
  /** Whether this is the last column — reserved for optional styling. */
  isLastColumn?: boolean;
  /** When `true`, the trend badge (icon + percentage) is hidden entirely. */
  hideTrend?: boolean;
  /** When `true`, the trend badge background is transparent instead of the default colored background. */
  hideTrendColor?: boolean;
}

const getColorConfig = (change: number): { color: string; backgroundColor: string; icon?: IconName } => {
  if (change > 0) {
    return {
      color: theme.colors.success50,
      backgroundColor: theme.colors.success5,
      icon: 'trend-rise',
    };
  } else if (change < 0) {
    return {
      color: theme.colors.critical50,
      backgroundColor: theme.colors.critical5,
      icon: 'trend-fall',
    };
  } else {
    return {
      color: theme.colors.neutral50,
      backgroundColor: theme.colors.neutral10,
      icon: undefined,
    };
  }
};

/**
 * A reusable cell renderer for location comparison tables.
 *
 * Renders a bold formatted value followed by a colored pill-style trend badge
 * (icon + percentage). Matches the established pattern used in AI Receptionist
 * and Appointment analytics location comparison tables.
 */
export const ValueWithTrend = ({
  cellValue,
  startAdornment,
  endAdornment,
  hideTrend,
  hideTrendColor,
}: ValueWithTrendProps) => {
  const { color, backgroundColor, icon } = getColorConfig(cellValue?.change ?? 0);
  const changeValue = Math.abs(cellValue?.change ?? 0);

  return (
    <div
      css={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        color: theme.colors.black,
        margin: theme.spacing(-1, -2),
        padding: theme.spacing(1, 2),
        boxSizing: 'content-box',
      }}
    >
      {/* Value right-aligned to the cell's centerline so numbers line up in a straight column across rows. */}
      <div
        css={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Text
          weight='bold'
          css={{
            color: 'inherit',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {startAdornment}
          {formatters.value(cellValue?.value ?? 0)}
          {endAdornment}
        </Text>
      </div>
      {/* Trend badge left-aligned from the centerline so badges share a column too. */}
      <div
        css={{
          flex: 1,
          minWidth: 0,
          minHeight: 26,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingLeft: theme.spacing(0.5),
        }}
      >
        {!hideTrend && changeValue > -100 && changeValue < 100 && (
          <span
            css={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              padding: theme.spacing(0.5, 1),
              borderRadius: theme.borderRadius.large,
              color: hideTrendColor ? theme.colors.neutral50 : color,
              backgroundColor: hideTrendColor ? theme.colors.neutral10 : backgroundColor,
              whiteSpace: 'nowrap',
            }}
          >
            {icon && <Icon name={icon} size={12} css={{ marginRight: theme.spacing(0.5) }} />}
            <Text
              size='small'
              css={{
                color: 'inherit',
              }}
            >
              {changeValue}%
            </Text>
          </span>
        )}
      </div>
    </div>
  );
};
/** A metric column definition for a location comparison table, plus the optional `ValueWithTrend` display flags. */
export type ValueWithTrendColumn<T extends object> = {
  id: string;
  Header: ReactNode;
  /** The `ValueWithTrendData` field on the row this column reads from. */
  accessor: keyof T & string;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
  startAdornment?: string;
  endAdornment?: string;
  /** When `true`, the trend badge (icon + percentage) is hidden for this column. */
  hideTrend?: boolean;
  /** When `true`, the trend badge background is transparent for this column. */
  hideTrendColor?: boolean;
};

/**
 * Wires a list of metric column definitions into `TableColumnConfig`s whose cells
 * render a `ValueWithTrend` badge. Each column's `accessor` must point at a
 * `ValueWithTrendData` field on the row.
 */
export const buildValueWithTrendColumns = <T extends object>(
  columns: ValueWithTrendColumn<T>[]
): TableColumnConfig<T>[] =>
  columns.map((col, idx) => {
    const { startAdornment, endAdornment, hideTrend, hideTrendColor, accessor, ...rest } = col;
    return {
      ...rest,
      sortType: 'number' as const,
      accessor: (item: T) => (item[accessor] as ValueWithTrendData | undefined)?.value ?? 0,
      cellRenderer: (_value: number, rowData?: T) => (
        <ValueWithTrend
          cellValue={
            (rowData?.[accessor] as ValueWithTrendData | undefined) ?? {
              value: 0,
              change: 0,
            }
          }
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          hideTrend={hideTrend}
          hideTrendColor={hideTrendColor}
          isLastColumn={idx === columns.length - 1}
        />
      ),
    };
    // react-table's recursive path/accessor generics can't be represented for an unresolved `T`.
  }) as unknown as TableColumnConfig<T>[];
